import { message } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import {
  isImageFile,
  isImageOverSize,
  compressImage,
  bytesToMB,
  type CompressOptions,
} from '@ifa/utils';

export interface UseImageCompressionOptions {
  /** 最大文件大小（MB），默认 3MB */
  maxSizeMB?: number;
  /** 压缩选项 */
  compressOptions?: CompressOptions;
  /** 确认对话框函数 */
  confirm?: (options: {
    title: string;
    content: string;
    okText: string;
    cancelText: string;
    onOk: () => void;
    onCancel: () => void;
  }) => void;
  /** 压缩前回调 */
  onBeforeCompress?: (file: File) => void;
  /** 压缩后回调 */
  onAfterCompress?: (originalFile: File, compressedFile: File) => void;
  /** 压缩失败回调 */
  onCompressError?: (error: Error) => void;
}

/**
 * 图片压缩 Hook
 * 提供图片大小检查和自动压缩功能
 */
export const useImageCompression = (options: UseImageCompressionOptions = {}) => {
  const {
    maxSizeMB = 10,
    compressOptions = { quality: 0.6 },
    confirm,
    onBeforeCompress,
    onAfterCompress,
    onCompressError,
  } = options;

  /**
   * 处理文件上传前的压缩逻辑
   * @param file 要上传的文件
   * @returns 处理后的文件，如果返回 false 则阻止上传
   */
  const handleBeforeUpload = async (file: RcFile): Promise<File | false> => {
    // 如果不是图片或未超过大小限制，直接返回原文件
    if (!isImageFile(file) || !isImageOverSize(file, maxSizeMB)) {
      return file;
    }

    const fileSizeMB = bytesToMB(file.size).toFixed(2);

    // 如果没有提供 confirm 函数，直接压缩
    if (!confirm) {
      try {
        onBeforeCompress?.(file);
        message.loading({ content: '正在壓縮處理中，請稍候...', key: 'compress', duration: 0 });
        const compressedFile = await compressImage(file, compressOptions);
        message.destroy('compress'); // 关闭 loading 提示
        onAfterCompress?.(file, compressedFile);
        return compressedFile;
      } catch (error) {
        message.error({ content: '壓縮處理失敗，請重新選擇檔案或聯繫技術支援', key: 'compress' });
        onCompressError?.(error as Error);
        return false;
      }
    }

    // 使用确认对话框
    return new Promise<File | false>((resolve) => {
      confirm({
        title: '圖片檔案過大',
        content: `當前圖片大小為 ${fileSizeMB}MB，已超過 ${maxSizeMB}MB 上載限制。\n\n系統將自動壓縮圖片，請確認是否繼續上載？`,
        okText: '壓縮上載',
        cancelText: '取消',
        onOk: async () => {
          try {
            onBeforeCompress?.(file);
            message.loading({ content: '正在壓縮處理中，請稍候...', key: 'compress', duration: 0 });
            const compressedFile = await compressImage(file, compressOptions);
            message.destroy('compress'); // 关闭 loading 提示
            onAfterCompress?.(file, compressedFile);
            resolve(compressedFile);
          } catch (error) {
            message.error({ content: '壓縮處理失敗，請重新選擇檔案或聯繫技術支援', key: 'compress' });
            onCompressError?.(error as Error);
            resolve(false);
          }
        },
        onCancel: () => {
          resolve(false);
        },
      });
    });
  };

  return {
    handleBeforeUpload,
  };
};
